import { DateTime } from 'luxon';
import { BookingPaymentStatus, BookingSource } from '../models/booking.model';
import colors from './styles';

export const ATHENS_TIMEZONE = 'Europe/Athens';

export const getEnumValues = <T extends { [key: string]: any }>(enumObj: T): (keyof T)[] => {
    return Object.keys(enumObj).filter(key => !isNaN(Number(enumObj[key]))) as (keyof T)[];
}

export function debounce<T extends (...args: any[]) => void>(
    func: T,
    delay: number
): (...args: Parameters<T>) => void {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    };
}

export const getErrorMessage = (error: any) => {
    return error?.response?.data?.errorMessage || error?.errorMessage || error?.message || 'An unknown error occurred';
}

export const getAthensDate = (date?: Date | null | string, toJSDate: boolean = true) => {
    let dateToReturn: DateTime;
    if (!date) {
        dateToReturn = DateTime.now().setZone(ATHENS_TIMEZONE);
    } else {
        dateToReturn = DateTime.fromJSDate(date instanceof Date ? date : new Date(date)).setZone(ATHENS_TIMEZONE);
    }
    return toJSDate ? dateToReturn.toJSDate() : dateToReturn;
};

export const areAthensDatesEqual = (date1: Date | null | string, date2: Date | null | string) => {
    if (!date1 || !date2) return false;
    const dt1 = getAthensDate(date1, false) as DateTime;
    const dt2 = getAthensDate(date2, false) as DateTime;
    return dt1.hasSame(dt2, 'day');
};

export const getStartOfDay = (date?: Date | null | string) => (getAthensDate(date, false) as DateTime).startOf('day').toJSDate();
export const getEndOfDay = (date?: Date | null | string) => (getAthensDate(date, false) as DateTime).endOf('day').toJSDate();

export const getMonthStart = (date?: Date | null | string) => (getAthensDate(date, false) as DateTime).startOf('month').toJSDate();
export const getMonthEnd = (date?: Date | null | string) => (getAthensDate(date, false) as DateTime).endOf('month').toJSDate();

export const getFormattedDate = (date?: Date | null | string, format: string = 'yyyy-MM-dd') => (getAthensDate(date, false) as DateTime).toFormat(format);

export const getDaysInMonth = (year: number, month: number) => {
    const dateTime = DateTime.fromObject({ year, month });
    return dateTime.daysInMonth;
};

export const getPaymentStatusColor = (status: BookingPaymentStatus): string => {
    switch (status) {
        case BookingPaymentStatus.CONFIRMED:
            return colors.appDarkBlue;
        case BookingPaymentStatus.PENDING:
            return colors.appDarkOrange;
        case BookingPaymentStatus.PAID:
            return colors.appEmerald;
        case BookingPaymentStatus.CANCELLED:
            return colors.appGray600;
        case BookingPaymentStatus.NOT_PAID:
            return colors.appDanger;
        case BookingPaymentStatus.PARTIALLY_PAID:
            return colors.appOrange;
        case BookingPaymentStatus.FAILED:
            return colors.appDanger;
        default:
            return colors.appDanger;
    }
};


export const getBookingSourceColor = (source: BookingSource) => {
    switch (source) {
        case BookingSource.WEBSITE:
            return colors.appDarkBlue;
        case BookingSource.BACKOFFICE:
            return colors.appSuccess;
        case BookingSource.DOOR:
            return colors.appDanger;
        case BookingSource.HOTEL:
            return colors.appGold;
        case BookingSource.WHATSAPP:
            return colors.appDarkPink;
        case BookingSource.GROUP_TOUR_LEADER:
            return colors.appOrange;
        case BookingSource.AGENCY:
            return colors.appRosePink;
        case BookingSource.PLATFORM_VIATOR:
            return colors.appViatorOrange;
        case BookingSource.PLATFORM_GETYOURGUIDE:
            return colors.appGetYourGuideRed;
        case BookingSource.PLATFORM_TICKETS:
            return colors.appDarkBlue;
        case BookingSource.PLATFORM_AIRBNB:
            return colors.appAirbnbCoral;
        case BookingSource.OTHER_PLATFORM:
            return colors.appGray600;
        case BookingSource.OTHER_SOURCE:
            return colors.appGray900;
        default:
            return colors.appGray600;
    }
};

export const THEME_COMPONENTS = {
    DatePickerInput: {
        defaultProps: {
            radius: 10
        }
    },
    NumberInput: {
        defaultProps: {
            radius: 10
        }
    },
    PasswordInput: {
        defaultProps: {
            radius: 10
        }
    },
    TextInput: {
        defaultProps: {
            radius: 10
        }
    },
    Select: {
        defaultProps: {
            radius: 10
        }
    },
    MultiSelect: {
        defaultProps: {
            radius: 10
        }
    },
    Textarea: {
        defaultProps: {
            radius: 10
        }
    },
    Button: {
        defaultProps: {
            radius: 10
        }
    }
};

/**
 * Gets the show time for a given date based on the month and day
 * This matches the backend logic for consistent behavior
 */
export const getShowTimeForDate = (date: Date | string | null): string => {
    const greekTime = getAthensDate(date, false) as DateTime;
    const month = greekTime.month; // Luxon months are 1-based
    const day = greekTime.day;

    if (month === 5) return '21:00';  // May
    if ([6, 7, 8].includes(month)) return '21:30';  // June, July, August
    if (month === 9) return '21:00';  // September
    if (month === 10) {  // October
        return day <= 15 ? '20:30' : '20:00';
    }
    return '20:00';  // Default time for other months
};